import {
  takeEvery,
  put,
  call,
  fork,
  all,
  takeLeading,
} from 'redux-saga/effects';
import * as actionTypes from 'constants/actionTypes';
import {
  syncFirebaseChildren,
  syncFirebasePath,
} from 'sagas/firebase/internal-lib';
import * as firebase from 'sagas/firebase/lib';

import {
  replaceAccountTheme,
  replaceAccountThemes,
} from 'sagas/account/themes';
import { replaceAccountUser, replaceAccountUsers } from 'sagas/account/users';

import { replaceAccountAllTimeStats } from 'sagas/account/allTimeStats';
import { replaceAccountStats } from 'sagas/account/stats';
import { replaceAccountGoalStats } from 'sagas/account/goalStats';
import { replaceAccountView, replaceAccountViews } from 'sagas/account/views';
import { replaceAccountPreviews } from 'sagas/account/previews';
import { replaceAccountIntegrations } from 'sagas/account/integrations';
import {
  replaceAccountChecklist,
  replaceAccountChecklists,
} from 'sagas/account/checklists';
import {
  replaceAccountUserEvent,
  replaceAccountUserEvents,
} from 'sagas/account/userEvents';
import { replaceFirebaseOnlineStatus } from 'sagas/account/onlineStatus';
import { fetchAccountMeta } from 'actions/account/meta';
import { fetchAccountCategories } from 'actions/account/categories';
import { fetchAccountTags } from 'actions/account/tags';
import { fetchAccountContentStatus } from 'actions/account/contentStatus';
import { fetchFlows } from 'sagas/account/flows';
import { fetchAllNPS } from 'sagas/account/satisfaction';
import { fetchInstalledDomains } from 'sagas/account/installedDomains';
import { fetchLocales } from 'sagas/account/locales';
import { fetchSegments } from 'sagas/account/segments';
import { fetchGoals } from 'sagas/account/goals';
import { fetchAccountInfo } from 'sagas/account/meta';
import { getAllProfileAttributeLabels } from 'entities/profile-attribute-labels';
import { getAllProfileAttributes } from 'entities/profile-attributes';
import { getAllGroupAttributes } from 'entities/group-attributes';
import { fetchExperiences } from 'entities/experiences';
import { fetchGates } from 'entities/gates';
import { fetchTrialStatus } from 'entities/trial-status';

export function* prefetchAccountData({ payload }) {
  const { accountId } = payload;

  yield all([
    put(fetchAccountMeta(accountId)),
    put(fetchAccountCategories()),
    put(fetchAccountTags()),
    put(fetchAccountContentStatus()),
    // NOT firebase
    call(fetchFlows),
    call(fetchAllNPS),
    call(fetchInstalledDomains),
    call(fetchLocales),
    call(fetchSegments),
    call(fetchGoals),
    call(getAllProfileAttributeLabels),
    call(getAllProfileAttributes),
    call(getAllGroupAttributes),
    call(fetchAccountInfo),
    call(fetchExperiences),
    call(fetchGates),
    call(fetchTrialStatus),
  ]);
}

/*
 * Whenever Firebase tells us that the data at a particular location reference changed, we need to
 * update the Redux state to reflect it.  Often, we'll already have that state, but it's still important
 * to keep in sync.
 *
 * All of the `fetch` functions that follow manage updating state based on updates sent to us
 * over the Firebase WebSocket (or wherever else, someday :D)
 *
 * `sync` actions only need to be called once per ${id}, since they use a redux-saga eventChannel to stay in sync.
 */

export function* syncAccountWithFirebase(action) {
  const { accountId } = action.payload;

  yield call(firebase.waitReady);

  yield call(prefetchAccountData, action);

  // We're using `syncFirebaseChildren` for some of these in order to
  // synchronize on the individual rule or step level. This means a write
  // to one child won't cause us to replace all children in state. For
  // certain pieces of data this doesn't matter because we're not writing
  // to them from here, but for things like rules and steps, which we are
  // updating from this app, this becomes important. Updates to two steps
  // in quick succession would end up triggering two full syncs if we were
  // syncing the entire tree for steps. If the timing works out badly,
  // the second step change might be overridden before it can be sent to
  // Firebase. Syncing on the child level avoids this by only updating the
  // child that has actually changed.

  yield fork(
    syncFirebaseChildren,
    `accounts/${accountId}/styles`,
    replaceAccountThemes,
    replaceAccountTheme,
    actionTypes.CANCEL_ACCOUNT_SYNC
  );

  yield fork(
    syncFirebaseChildren,
    `accounts/${accountId}/users`,
    replaceAccountUsers,
    replaceAccountUser,
    actionTypes.CANCEL_ACCOUNT_SYNC
  );

  yield fork(
    syncFirebasePath,
    `accounts/${accountId}/account-stats`,
    replaceAccountAllTimeStats,
    actionTypes.CANCEL_ACCOUNT_SYNC
  );

  yield fork(
    syncFirebasePath,
    `accounts/${accountId}/flow-stats`,
    replaceAccountStats,
    actionTypes.CANCEL_ACCOUNT_SYNC
  );

  yield fork(
    syncFirebasePath,
    `accounts/${accountId}/goal-stats`,
    replaceAccountGoalStats,
    actionTypes.CANCEL_ACCOUNT_SYNC
  );

  yield fork(
    syncFirebaseChildren,
    `accounts/${accountId}/views/users`,
    replaceAccountViews,
    replaceAccountView,
    actionTypes.CANCEL_ACCOUNT_SYNC
  );

  yield fork(
    syncFirebasePath,
    `accounts/${accountId}/previews`,
    replaceAccountPreviews,
    actionTypes.CANCEL_ACCOUNT_SYNC
  );

  yield fork(
    syncFirebasePath,
    `accounts/${accountId}/integrations`,
    replaceAccountIntegrations,
    actionTypes.CANCEL_ACCOUNT_SYNC
  );

  yield fork(
    syncFirebaseChildren,
    `accounts/${accountId}/checklists`,
    replaceAccountChecklists,
    replaceAccountChecklist,
    actionTypes.CANCEL_ACCOUNT_SYNC
  );

  yield fork(
    syncFirebaseChildren,
    `accounts/${accountId}/user-events`,
    replaceAccountUserEvents,
    replaceAccountUserEvent,
    actionTypes.CANCEL_ACCOUNT_SYNC
  );

  yield fork(
    syncFirebasePath,
    '.info/connected',
    replaceFirebaseOnlineStatus,
    actionTypes.CANCEL_USER_SYNC
  );
}

export default function* syncAccount() {
  yield takeEvery(
    actionTypes.SYNC_ACCOUNT_WITH_FIREBASE,
    syncAccountWithFirebase
  );
  yield takeLeading(actionTypes.PREFETCH_ACCOUNT_DATA, prefetchAccountData);
}
